'use client';

import { useEffect } from 'react';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { type CliplisterQueryVariables } from '@packages/gql/generated/shopping/graphql';
import { GlycerinVideoAction } from '@packages/tracking/src/types/enums';
import { defaultPlayerConfig } from './CliplisterVideo.client.config';
import { useVideoTracking } from '../../Tracking/hooks/useVideoTracking';

export type CliplisterVideoProps = Pick<CliplisterQueryVariables, 'orderNumber'>;

export const CliplisterVideo = ({ orderNumber }: CliplisterVideoProps) => {
  const {
    product: {
      cliplister: { customerId },
    },
  } = useConfig();
  const { trackGlycerinVideoClick, trackGlycerinVideoProgress, trackVideoProgress } =
    useVideoTracking();

  const parentId = `cliplister-viewer-${orderNumber}`;

  useEffect(() => {
    if (window?.Cliplister) {
      const playerConfig = structuredClone(defaultPlayerConfig);

      playerConfig.parentId = parentId;
      playerConfig.customer = customerId;
      playerConfig.assetKeys = [orderNumber];

      const viewer = new window.Cliplister.Viewer(playerConfig);

      viewer.onReady(() => {
        const currentVideoAsset = viewer.getAssets()[viewer.getCurrentAssetIndex()];
        const progressPayload: Omit<Parameters<typeof trackGlycerinVideoProgress>[0], 'progress'> =
          {
            totalDuration: currentVideoAsset.duration,
            source: currentVideoAsset.uri,
            videoId: currentVideoAsset.id,
          };

        // add tracking events to viewer
        viewer.onPlay(() => {
          trackGlycerinVideoClick({
            videoId: currentVideoAsset.id,
            source: currentVideoAsset.uri,
            action: GlycerinVideoAction.PLAY,
          });
          trackVideoProgress('start');
        });
        viewer.onPause(() => {
          trackGlycerinVideoClick({
            videoId: currentVideoAsset.id,
            source: currentVideoAsset.uri,
            action: GlycerinVideoAction.PAUSE,
          });
        });
        viewer.onProgress20(() => {
          trackGlycerinVideoProgress({ progress: 20, ...progressPayload });
          trackVideoProgress('25');
        });
        viewer.onProgress40(() => {
          trackGlycerinVideoProgress({ progress: 40, ...progressPayload });
        });
        viewer.onProgress50(() => {
          trackVideoProgress('50');
        });
        viewer.onProgress60(() => {
          trackGlycerinVideoProgress({ progress: 60, ...progressPayload });
        });
        viewer.onProgress80(() => {
          trackGlycerinVideoProgress({ progress: 80, ...progressPayload });
          trackVideoProgress('75');
        });
        viewer.onFinish(() => {
          trackGlycerinVideoProgress({ progress: 100, ...progressPayload });
          trackVideoProgress('100');
        });
      });

      return () => {
        // cleanup runs before a new useEffect call so it's fine to destroy the current viewer instance here
        viewer.destroy();
      };
    }
    return () => {};
  }, [
    orderNumber,
    trackGlycerinVideoClick,
    trackGlycerinVideoProgress,
    trackVideoProgress,
    customerId,
    parentId,
  ]);

  return (
    <div className="cursor-default">
      <div id={parentId} />
    </div>
  );
};
